import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsShapeType, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { BaseEntity } from './BaseEntity';

export interface LauncherEntityParams
{
    type: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
    props: {
        power?: number
    }
}

export class LauncherEntity extends BaseEntity
{
    public top: Sprite;
    public base: Sprite;
    public pistons: Sprite;
    public params!: LauncherEntityParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;
    public launched = false;

    private wheelLeft = false;
    private wheelRight = false;
    private chassis = false;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.culling = true;

        this.pistons = Sprite.from(Shared.assets.getTexture('launcher-pistons'));
        this.pistons.anchor.set(0.5);
        this.view.addChild(this.pistons);

        this.base = Sprite.from(Shared.assets.getTexture('launcher-base'));
        this.base.anchor.set(0.5);
        this.view.addChild(this.base);

        this.top = Sprite.from(Shared.assets.getTexture('launcher-top'));
        this.top.anchor.set(0.5);
        this.view.addChild(this.top);
    }

    public build(params: LauncherEntityParams): void
    {
        this.physics.clearParts();
        this.params = params;

        const absx = Math.abs(this.params.sx);
        const absy = Math.abs(this.params.sy);

        const platformBody = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y + (2 * this.params.sy),
            mass: 0,
        }, {
            type: PhysicsShapeType.BOX,
            width: 178 * absx,
            height: 10 * absy,
            group: PhysicsCollisionGroup.LEVEL,
            mask: PhysicsCollisionGroup.VEHICLE,
        });

        const triggerBody = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y - (80 * this.params.sy),
            mass: 0,
        }, {
            type: PhysicsShapeType.BOX,
            width: 100 * absx,
            height: 120 * absy,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
            sensor: true,
        });

        this.physics.addParts(platformBody, triggerBody);
        this.physics.setAngle(this.params.r);

        this.view.scale.set(this.params.sx, this.params.sy);

        this.reactivate();
        this.culling.updateBounds();
    }

    public levelRespawn(): void
    {
        this.reactivate();
    }

    protected onBeginContact(other: p2.Body): void
    {
        if (this.launched || !other.physicsComponent) return;
        const game = this.scene as Game;

        if (other.physicsComponent?.entity === game.vehicle.wheelLeft) this.wheelLeft = true;
        if (other.physicsComponent?.entity === game.vehicle.wheelRight) this.wheelRight = true;
        if (other.physicsComponent?.entity === game.vehicle.chassis) this.chassis = true;
        this.launch();
    }

    private launch(): void
    {
        if (!this.wheelLeft || !this.wheelRight || !this.chassis) return;
        if (this.launched) return;
        this.launched = true;

        const game = this.scene as Game;

        const angle = -this.params.r;
        const power = this.params.props.power || 650;
        const force = [-Math.sin(angle) * power, Math.cos(angle) * power];

        game.vehicle.chassis.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.chassis.physics.mainBody.velocity[1] *= 0.5;
        game.vehicle.wheelLeft.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.wheelLeft.physics.mainBody.velocity[1] *= 0.5;
        game.vehicle.wheelRight.physics.mainBody.velocity[0] *= 0.5;
        game.vehicle.wheelRight.physics.mainBody.velocity[1] *= 0.5;

        game.vehicle.chassis.physics.mainBody.applyImpulse(force);
        game.vehicle.wheelLeft.physics.mainBody.applyImpulse(force);
        game.vehicle.wheelRight.physics.mainBody.applyImpulse(force);

        game.soundSystem.playSfx('launcher', 1);
        this.animateLaunch();

        game.physics.timer.dispatchAfter(3, () => this.reactivate());
    }

    private reactivate(): void
    {
        this.killTweens();
        this.top.scale.y = 0;
        this.top.y = 0;
        this.pistons.y = 0;
        this.pistons.scale.y = 0;
        this.wheelLeft = false;
        this.wheelRight = false;
        this.chassis = false;
        this.launched = false;
    }

    private animateLaunch(): void
    {
        this.killTweens();

        const topY = -95;
        const duration = 0.15;
        const ease = 'linear';

        this.game?.tween.to(this.top.scale, { y: 1, duration, ease });
        this.game?.tween.to(this.top, { y: topY, duration, ease });
        this.game?.tween.to(this.pistons.scale, { y: 1, duration, ease });
        this.game?.tween.to(this.pistons, { y: topY + 50, duration, ease });

        this.game?.physics.timer.dispatchAfter(1, () => this.animateRearm());
    }

    private animateRearm(): void
    {
        this.killTweens();

        const duration = 0.3;
        const ease = 'sine.inOut';

        this.game?.tween.to(this.top.scale, { y: 0, duration, ease });
        this.game?.tween.to(this.top, { y: 0, duration, ease });
        this.game?.tween.to(this.pistons.scale, { y: 0.001, duration, ease });
        this.game?.tween.to(this.pistons, { y: 0, duration, ease });
    }

    private killTweens(): void
    {
        this.game?.tween.kill(this.top.scale);
        this.game?.tween.kill(this.top);
        this.game?.tween.kill(this.pistons.scale);
        this.game?.tween.kill(this.pistons);
    }
}

export class LauncherFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'launcher';
    }

    public static create(game: Game, data: Record<string, any>): void
    {
        const entity = game.pool.get(LauncherEntity) as LauncherEntity;

        entity.build(data as LauncherEntityParams);

        game.level.addEntity(entity);
    }
}
